<template>
    <div class="mt-3">
        <div class="flex flex-col gap-3 md:grid md:grid-cols-3 md:items-center">
            <div class="hidden md:block"></div>
            <h1 class="text-3xl text-center">
                Usuarios en total
                <strong class="text-theme-primary">
                    {{ itemsNumber }}
                </strong>
            </h1>
            <div class="flex justify-center md:justify-end">
                <app-button
                    v-if="$ability.hasPermission('user:Create')"
                    color="success"
                    size="large"
                    @click="$router.push('/app/users/new')"
                >
                    <span class="hidden md:inline">Nuevo usuario</span>
                    <span class="md:hidden font-bold">+</span>
                </app-button>
            </div>
        </div>

        <div class="flex flex-wrap">
            <div class="w-full">
                <users-filters v-model="query" class="mt-2" />
                <div class="flex justify-center mt-2">
                    <app-button v-show="query" relief @click="reloadItems">
                        Consultar 🔎
                    </app-button>
                </div>
            </div>
            <div class="flex justify-between flex-col lg:flex-row w-full mt-3">
                <div class="flex items-center justify-around"></div>
                <div class="flex items-center justify-around">
                    <list-settings-button
                        :value="{ pageSize }"
                        @change="onListSettingsChange"
                    />
                    <custom-pagination
                        v-model="currentPage"
                        :length="pagesNumber"
                        @input="listItems()"
                    />

                    <tooltip-redirect
                        v-if="$ability.hasPermission('user:Create')"
                        to="/app/users/new"
                        message="Crear usuario"
                    />
                </div>
            </div>
        </div>

        <div class="flex flex-wrap mt-4">
            <custom-table
                ref="selectableTable"
                :fields="$data.$fields"
                :items="itemsCurrent"
                :per-page="itemsCurrent.length"
                select-mode="multi"
                responsive
                outlined
                no-border-collapse
                hover
                selectable
                class="w-full"
                items-empty-message="Nuestro sistema NO encuentra resultados. Intenta modificar los filtros de búsqueda."
                @flex-selected="onRowSelected"
            >
                <template #cell(id)="data">
                    <div class="flex items-center gap-2">
                        <router-link
                            v-if="$ability.hasPermission('user:Get', true)"
                            :to="`/app/users/detail/${data.item.id}`"
                            class="text-theme-primary"
                            title="Ver detalle"
                        >
                            <chevron-right-icon class="w-4 h-4" />
                        </router-link>
                        <uuid-display :value="data.item.id" />
                    </div>
                </template>
                <template #cell(identificationNumber)="data">
                    <span class="text-center text-uppercase">
                        {{ data.item.identificationType }}
                        {{ data.item.identificationNumber || "NA" }}
                    </span>
                </template>
                <template #cell(firstName)="data">
                    <span class="text-center capitalize">
                        {{ data.item.firstName }} {{ data.item.lastName }}
                    </span>
                </template>
                <template #cell(tenantName)="data">
                    <span class="text-center">
                        {{ data.item.tenantName || "NA" }}
                    </span>
                </template>
                <template #cell(email)="data">
                    <span class="text-center">
                        {{ data.item.email }}
                    </span>
                </template>
                <template #cell(phone)="data">
                    <span class="text-center">
                        {{ data.item.phone || "NA" }}
                    </span>
                </template>
                <template #cell(createdAt)="data">
                    <span class="text-center">
                        {{ data.item.createdAt | date }}
                    </span>
                </template>
            </custom-table>
        </div>
    </div>
</template>

<script>
import urlQuerySyncMixin from "@/mixins/urlQuerySyncMixin";
import { mapActions, mapGetters } from "vuex";

import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import UsersFilters from "@/components/users/UsersFilters.vue";
import CustomTable from "@/components/utils/CustomTable.vue";
import TooltipRedirect from "@/components/utils/TooltipRedirect.vue";
import UuidDisplay from "@/components/utils/UuidDisplay.vue";

export default {
    name: "IndexUsers",
    components: {
        UsersFilters,
        TooltipRedirect,
        CustomTable,
        ChevronRightIcon,
        UuidDisplay
    },
    mixins: [urlQuerySyncMixin],
    data: () => ({
        itemsCurrent: [],
        selected: [],
        currentPage: 1,
        pagesNumber: 0,
        itemsNumber: 0,
        pageSize: 24,
        $fields: [
            { key: "id", label: "ID", sortable: false },
            {
                key: "identificationNumber",
                label: "Identificación",
                sortable: false
            },
            {
                key: "firstName",
                label: "Nombres",
                sortable: false
            },
            {
                key: "email",
                label: "Correo",
                sortable: false
            },
            {
                key: "phone",
                label: "Célular",
                sortable: false
            },
            { key: "createdAt", label: "Creación", sortable: true }
        ],
        query: {},
        $urlPaginationKey: "page",
        $urlFilterSchema: [
            { key: "phone", type: "string" },
            { key: "email", type: "string" },
            { key: "identificationNumber", type: "string" }
        ]
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {},
    async mounted() {
        const verifyPagePermission =
            this.$ability.verifyPagePermission.bind(this);
        if (!(await verifyPagePermission("user:List", "/app", false, true)))
            return;
        this.listItems(true);
    },
    methods: {
        ...mapActions("users", ["listUsersPaginated"]),
        async listItems(complete = false) {
            this.isLoading = true;
            this.$loading.show();
            try {
                const { items, pagination } = await this.listUsersPaginated({
                    ...this.query,
                    complete,
                    itemsByPage: this.pageSize,
                    page: this.currentPage
                });
                this.itemsCurrent = items;
                if (pagination) {
                    this.itemsNumber = pagination.itemsNumber;
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.$loading.hide();
            } catch (error) {
                this.$loading.hide();
                this.isLoading = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async reloadItems() {
            this.currentPage = 1;
            await this.listItems(true);
        },
        onListSettingsChange({ pageSize }) {
            this.pageSize = pageSize;
            this.reloadItems();
        },
        onRowSelected(items) {
            this.selected = items;
        },
        clearSelected() {
            this.$refs.selectableTable.clearSelected();
            this.selected = [];
        }
    }
};
</script>
